import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Shared "admin surface" building blocks (see styles/adminSurface.css). These bring
// every admin page into the governance visual language used by the Legal Holds page:
// gradient hero, custom dropdowns, toggle switches, styled empty states.

// Gradient hero header with icon, title, subtitle, an optional stat, and an
// optional refresh button.
// Circular-arrow refresh glyph — replaces the bare ↺/⟳ characters so the button
// reads as a real icon (and spins while refreshing).
function RefreshIcon({ spinning }) {
  return (
    <svg className={`asf-refresh__ico${spinning ? " spin" : ""}`} viewBox="0 0 24 24"
      width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <polyline points="21 3 21 9 15 9" />
    </svg>
  );
}

export function Hero({ icon, title, subtitle, statValue, statLabel, statDotColor = "#4ade80", onRefresh, refreshing, actions, art, logo }) {
  return (
    <div className="asf-hero">
      <div className="asf-hero__glow" />
      {logo != null && <div className="asf-hero__logo" aria-hidden="true">{logo}</div>}
      {icon != null && !art && !logo && <div className="asf-hero__icon">{icon}</div>}
      <div className="asf-hero__body">
        <h1 className="asf-hero__title">{title}</h1>
        {subtitle && <p className="asf-hero__sub">{subtitle}</p>}
      </div>
      {statValue != null && (
        <div className="asf-hero__stat">
          <b>{statValue}</b>
          {statLabel && <span><i className="asf-status__dot" style={{ background: statDotColor }} /> {statLabel}</span>}
        </div>
      )}
      {actions && <div className="asf-hero__actions">{actions}</div>}
      {onRefresh && (
        <button className="asf-refresh" onClick={onRefresh} disabled={refreshing} title="Refresh">
          <RefreshIcon spinning={refreshing} />
        </button>
      )}
      {art && !logo && <div className="asf-hero__art" aria-hidden="true">{art}</div>}
    </div>
  );
}

// Custom dropdown that matches the surface (native <select> looks out of place
// against the gradient). Closes on outside-click. options: [{value,label}].
/**
 * The menu is rendered into <body> rather than beside the button.
 *
 * It used to be `position: absolute` inside the field, which works until an ancestor clips it — and
 * `.asf-tablecard` is `overflow: hidden` for its rounded corners, so any dropdown placed inside an
 * admin table had its list cut off at the card's edge. No z-index escapes an overflow clip. The list
 * still scrolled internally, so the options were there and simply could not be reached.
 *
 * Portalled and positioned in viewport coordinates, no ancestor can clip it. The trade is that it no
 * longer moves with the page, so it closes on scroll and resize rather than drifting away from its
 * button — and the outside-click test has to consider both nodes, since the menu is no longer inside
 * the field it belongs to.
 */
export function Dropdown({ value, onChange, options, placeholder, icon, disabled, emptyLabel }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);

  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const MENU_MAX = 288;
    // Flip above when there is not room below and there is more room above, so the last option is
    // reachable near the bottom of the window instead of hanging off it.
    const up = below < Math.min(MENU_MAX, 200) && r.top > below;
    setRect({
      left: r.left,
      width: r.width,
      top: up ? null : r.bottom + 6,
      bottom: up ? window.innerHeight - r.top + 6 : null,
      maxHeight: Math.max(120, (up ? r.top : below) - 16),
    });
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    const close = () => setOpen(false);
    // `true` so a scroll inside any container closes it, not only a scroll of the window.
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, place]);
  const list = options || [];
  // A dropdown with nothing in it used to open an empty white box: it looked broken rather than empty,
  // and gave the operator no idea that the list is populated somewhere else. With no options it is
  // disabled and says so, so "nothing to choose" is visible without clicking.
  const isEmpty = list.length === 0;
  const sel = list.find((o) => String(o.value) === String(value));
  return (
    <div className="asf-dd" ref={ref}>
      <button type="button" className={`asf-dd__btn${open && !isEmpty ? " open" : ""}`}
        disabled={disabled || isEmpty} onClick={() => { if (!isEmpty) setOpen((o) => !o); }}>
        {icon && <span className="asf-dd__ico">{icon}</span>}
        <span className={`asf-dd__lbl${sel ? "" : " ph"}`}>
          {sel ? sel.label : (isEmpty ? (emptyLabel || placeholder) : placeholder)}
        </span>
        <span className="asf-dd__chev">▾</span>
      </button>
      {open && !isEmpty && rect && createPortal(
        <div ref={menuRef} className="asf-dd__menu asf-dd__menu--fixed"
          style={{
            left: rect.left,
            width: rect.width,
            ...(rect.top != null ? { top: rect.top } : { bottom: rect.bottom }),
            maxHeight: rect.maxHeight,
          }}>
          {list.map((o) => {
            const on = String(o.value) === String(value);
            return (
              <div key={o.value} className={`asf-dd__opt${on ? " sel" : ""}`}
                onClick={() => { onChange(o.value); setOpen(false); }}>
                <span className="asf-dd__dot" />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{o.label}</span>
                <span className="asf-dd__check">✓</span>
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

// A labelled toggle switch (replaces a bare checkbox).
export function ToggleSwitch({ checked, onChange, label }) {
  return (
    <label className="asf-switch">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="asf-switch__track" />
      {label}
    </label>
  );
}

// Segmented control. options: [{value,label}].
export function SegControl({ value, onChange, options }) {
  return (
    <div className="asf-seg" role="tablist">
      {options.map((o) => (
        <button key={o.value} type="button" className={String(value) === String(o.value) ? "on" : ""}
          onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

// Styled empty state with an icon ring.
export function EmptyState({ icon, title, hint }) {
  return (
    <div className="asf-empty">
      <div className="asf-empty__ring">{icon}</div>
      <div className="asf-empty__title">{title}</div>
      {hint && <div className="asf-empty__hint">{hint}</div>}
    </div>
  );
}
